import React, { useState, useEffect } from "react";
import { X, Crown, Check, Star, Lightning, CreditCard, CurrencyBtc, ArrowLeft, CaretRight, Spinner } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";

// Centralized Early Access pricing table for GENI PRO (Fixed Commercial Values)
export const EARLY_ACCESS_PRICES = {
  BRL: {
    monthly: { amount: 49.90, formatted: "R$49,90", interval: "por mês", periodText: "Cobrança mensal sem fidelidade" },
    yearly: { amount: 442.00, formatted: "R$442,00", interval: "por ano", periodText: "Acesso anual com desconto" },
    lifetime: { amount: 1970.00, formatted: "R$1.970,00", interval: "pagamento único", periodText: "Acesso PRO vitalício sem mensalidades" }
  },
  USD: {
    monthly: { amount: 10, formatted: "US$10", interval: "por mês", periodText: "Monthly recurring billing" },
    yearly: { amount: 89, formatted: "US$89", interval: "por ano", periodText: "Annual access with discount" },
    lifetime: { amount: 394, formatted: "US$394", interval: "pagamento único", periodText: "Lifetime PRO access without recurring fees" }
  },
  CRYPTO: {
    monthly: { amount: 10, formatted: "10 USDT", interval: "por mês", periodText: "USDT (BEP20) monthly billing" },
    yearly: { amount: 89, formatted: "89 USDT", interval: "por ano", periodText: "USDT (BEP20) annual access" },
    lifetime: { amount: 394, formatted: "394 USDT", interval: "pagamento único", periodText: "USDT (BEP20) one-time payment" }
  }
};

export const OPTIONS_PRO_BENEFITS = [
  "Complete operations history",
  "Advanced performance & capital analysis",
  "Margin × Volatility Analysis",
  "Delta & Price Alerts",
  "Roll analysis & position cycles",
  "BTC Outlook & Futures Hedge",
];

const STRIPE_PRICES = EARLY_ACCESS_PRICES.BRL;

export default function UpgradeModal({ 
  isOpen, 
  onClose, 
  onSelectPlan,
  hasPendingPayment = false,
  mode = null,
  isUpgrade = false,
  upgradePrice = 79,
  userEmail = null,
  context = 'trade',
  returnContext = 'trade',
  content = null,
}) {
  // Step: 'method' = payment method selection, 'stripe' = BRL plans, 'redirecting' = going to Stripe
  const [step, setStep] = useState('method');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [currency, setCurrency] = useState('BRL');
  const isOptionsContext = context === 'options';

  const copy = {
    optionsBadge: content?.optionsBadge || 'GENI Options PRO',
    title: content?.title || 'GENI OPTIONS PRO',
    description: content?.description || 'Unlock the full options experience with the same global PRO subscription.',
    benefitsTitle: content?.benefitsTitle || 'PRO BENEFITS',
    benefits: isOptionsContext
      ? OPTIONS_PRO_BENEFITS
      : content?.benefits && content?.benefits.length > 0
        ? content.benefits
        : OPTIONS_PRO_BENEFITS,
    disclaimer: content?.disclaimer || '',
    monthlyPlanLabel: content?.monthlyPlanLabel || 'PRO Monthly',
    yearlyPlanLabel: content?.yearlyPlanLabel || 'PRO Yearly',
    lifetimePlanLabel: content?.lifetimePlanLabel || 'PRO Lifetime',
    monthlyIntervalLabel: content?.monthlyIntervalLabel || '/ month',
    yearlyIntervalLabel: content?.yearlyIntervalLabel || '/ year',
    monthlyButtonLabel: content?.monthlyButtonLabel || 'Upgrade to PRO Monthly',
    yearlyButtonLabel: content?.yearlyButtonLabel || 'Upgrade to PRO Yearly',
    otherMethodLabel: content?.otherMethodLabel || 'Use a different payment method',
    bestValueLabel: content?.bestValueLabel || 'BEST VALUE',
    paymentNote: content?.paymentNote || 'Secure BRL checkout via the existing subscription flow.',
    redirectingTitle: content?.redirectingTitle || 'Redirecting to checkout...',
    redirectingBody: content?.redirectingBody || "You'll be redirected to Stripe to complete your payment securely.",
  };

  // Reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep(isOptionsContext ? 'stripe' : 'method');
      setIsLoading(false);
      setSelectedPlan('yearly');
    }
  }, [isOpen, isOptionsContext]);

  useEffect(() => {
    if (isOpen) {
      setStep(isOptionsContext ? 'stripe' : 'method');
    }
  }, [isOpen, isOptionsContext]);

  if (!isOpen || hasPendingPayment) return null;

  const isYearlyOnly = mode === 'yearly-only';

  // Get current active price set based on currency tab
  const activePrices = EARLY_ACCESS_PRICES[currency] || EARLY_ACCESS_PRICES.BRL;

  // Centralized plan action dispatcher
  const handlePlanAction = (plan) => {
    if (currency === 'BRL') {
      if (plan === 'lifetime') {
        toast.info("As inscrições para o plano Lifetime serão liberadas em breve no Early Access.");
      } else {
        handleStripeCheckout(plan);
      }
    } else if (currency === 'USD') {
      toast.info(`O checkout em USD (${activePrices[plan]?.formatted}) será habilitado em breve.`);
    } else if (currency === 'CRYPTO') {
      if (isOptionsContext) {
        onSelectPlan(plan, isUpgrade, upgradePrice);
      } else {
        toast.info(`O checkout direto em USDT (${activePrices[plan]?.formatted}) será habilitado em breve.`);
      }
    }
  };

  // Handle payment method selection
  const handleSelectMethod = (method) => {
    if (method === 'crypto') {
      // For crypto, pass 'yearly' as default - the CryptoPaymentModal allows plan selection
      onSelectPlan('yearly', isUpgrade, upgradePrice);
    } else {
      // For Stripe, show BRL plans
      setStep('stripe');
    }
  };

  // Handle Stripe checkout
  const handleStripeCheckout = async (plan) => {
    setSelectedPlan(plan);
    setIsLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await api.post('/payments/stripe/create-checkout-session', {
        plan_type: plan,
        origin_url: originUrl,
        return_context: returnContext,
      });
      
      if (response.data.checkout_url) {
        // Show redirecting state
        setStep('redirecting');
        // Small delay to show the redirecting message before navigating
        setTimeout(() => {
          window.location.href = response.data.checkout_url;
        }, 100);
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('[Stripe] Checkout error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create checkout session';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  // Go back to method selection
  const handleBack = () => {
    setStep('method');
    setIsLoading(false);
  };

  // STEP: Payment Method Selection
  if (step === 'method') {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="upgrade-modal-overlay"
      >
        <div 
          className="bg-[#121212] border border-white/10 rounded-sm p-8 w-full max-w-md mx-4 relative"
          data-testid="upgrade-modal"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            data-testid="upgrade-modal-close"
          >
            <X size={20} weight="bold" />
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown size={32} weight="fill" className="text-[#FFD700]" />
            </div>
            <h2 className="text-2xl font-black font-heading tracking-tight mb-2">
              {isUpgrade ? "Upgrade to Yearly" : "Become PRO"}
            </h2>
            <p className="text-white/60">Choose your payment method</p>
          </div>

          <div className="space-y-3 mb-6">
            {/* Credit Card Option - Active for all users */}
            <button
              onClick={() => handleSelectMethod('stripe')}
              className="w-full bg-[#0A0A0A] border border-white/10 hover:border-[#635BFF]/50 rounded-sm p-4 flex items-center justify-between transition-all group"
              data-testid="payment-method-stripe"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#635BFF]/10 rounded-full flex items-center justify-center">
                  <CreditCard size={24} weight="duotone" className="text-[#635BFF]" />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold flex items-center gap-2">
                    Credit Card
                    <span className="text-[10px] bg-[#635BFF]/20 text-[#635BFF] px-2 py-0.5 rounded-full font-bold">BRL</span>
                  </div>
                  <div className="text-white/50 text-sm">Pay with credit card</div>
                </div>
              </div>
              <CaretRight size={20} className="text-white/30 group-hover:text-white/60 transition-colors" />
            </button>

            {/* Crypto Option - Active */}
            <button
              onClick={() => handleSelectMethod('crypto')}
              className="w-full bg-[#0A0A0A] border border-white/10 hover:border-[#F0B90B]/50 rounded-sm p-4 flex items-center justify-between transition-all group"
              data-testid="payment-method-crypto"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F0B90B]/10 rounded-full flex items-center justify-center">
                  <CurrencyBtc size={24} weight="duotone" className="text-[#F0B90B]" />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold flex items-center gap-2">
                    Crypto
                    <span className="text-[10px] bg-[#F0B90B]/20 text-[#F0B90B] px-2 py-0.5 rounded-full font-bold">USDT</span>
                  </div>
                  <div className="text-white/50 text-sm">Pay with USDT (BEP20)</div>
                </div>
              </div>
              <CaretRight size={20} className="text-white/30 group-hover:text-white/60 transition-colors" />
            </button>
          </div>

          <p className="text-center text-white/40 text-xs">
            Credit card payments will be available soon.
          </p>
        </div>
      </div>
    );
  }

  // STEP: Stripe/BRL Plan Selection
  if (step === 'stripe') {
    if (isOptionsContext) {
      return (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-3"
          onClick={(e) => { if (e.target === e.currentTarget && !isLoading) onClose(); }}
          data-testid="options-upgrade-modal-overlay"
        >
          <div 
            className="relative w-full max-w-6xl max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-2xl border border-white/10 bg-[#07111F] text-white shadow-[0_32px_120px_rgba(0,0,0,0.52)] sm:max-h-[calc(100dvh-1.5rem)]"
            data-testid="options-upgrade-modal"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(240,185,11,0.18),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(96,165,250,0.14),_transparent_36%)]" />
            <button
              onClick={onClose}
              disabled={isLoading}
              className="absolute right-4 top-4 z-10 text-white/45 transition-colors hover:text-white disabled:opacity-50"
              data-testid="options-upgrade-modal-close"
            >
              <X size={20} weight="bold" />
            </button>

            <div className="relative grid gap-5 p-4 sm:p-5 lg:p-6 xl:grid-cols-[minmax(0,1fr),320px]">
              <div className="min-w-0 space-y-4" data-testid="options-upgrade-modal-content">
                <div className="space-y-2.5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-[#F0B90B]/25 bg-[#F0B90B]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#F6D365]" data-testid="options-upgrade-modal-badge">
                    <Crown size={14} weight="fill" />
                    {copy.optionsBadge}
                  </div>
                  <div className="max-w-3xl space-y-1.5">
                    <h2 className="pr-8 text-3xl font-black text-white sm:text-4xl" data-testid="options-upgrade-modal-title">{copy.title}</h2>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-[#F6D365]" data-testid="options-upgrade-early-access-text">
                        Condições especiais para participantes do Early Access.
                      </p>
                      <p className="text-xs text-white/50" data-testid="options-upgrade-early-access-subtext">
                        Disponíveis até 31 de dezembro de 2026.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Currency / Payment Selector */}
                <div className="flex flex-wrap items-center gap-2" data-testid="options-currency-selector-wrapper">
                  <span className="text-xs font-medium text-white/50">Pagar em:</span>
                  <div className="inline-flex items-center rounded-lg border border-white/10 bg-black/40 p-0.5">
                    <button
                      type="button"
                      onClick={() => setCurrency('BRL')}
                      className={`rounded-md px-3 py-1 text-xs font-bold transition-colors ${
                        currency === 'BRL'
                          ? 'bg-white text-black shadow-sm'
                          : 'text-white/60 hover:text-white'
                      }`}
                      data-testid="currency-selector-brl"
                    >
                      BRL
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrency('USD')}
                      className={`rounded-md px-3 py-1 text-xs font-bold transition-colors ${
                        currency === 'USD'
                          ? 'bg-white text-black shadow-sm'
                          : 'text-white/60 hover:text-white'
                      }`}
                      data-testid="currency-selector-usd"
                    >
                      USD
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrency('CRYPTO')}
                      className={`rounded-md px-3 py-1 text-xs font-bold transition-colors ${
                        currency === 'CRYPTO'
                          ? 'bg-white text-black shadow-sm'
                          : 'text-white/60 hover:text-white'
                      }`}
                      data-testid="currency-selector-crypto"
                    >
                      CRYPTO
                    </button>
                  </div>
                </div>

                <div className="grid items-stretch gap-3 md:grid-cols-3" data-testid="options-upgrade-plans-grid">
                  {/* 1. Monthly Plan */}
                  <div className="flex h-full min-w-0 flex-col rounded-2xl border border-white/10 bg-black/25 p-4 backdrop-blur-sm" data-testid="options-upgrade-plan-monthly-card">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/45" data-testid="options-upgrade-plan-monthly-label">MONTHLY</p>
                        <span className="rounded-full border border-white/10 px-2 py-0.5 text-[9px] font-medium text-white/40">Early Access</span>
                      </div>
                      <div className="pt-1.5">
                        <div className="whitespace-nowrap text-2xl font-black text-white lg:text-3xl" data-testid="options-upgrade-plan-monthly-price">
                          {activePrices.monthly.formatted}
                        </div>
                        <div className="text-xs text-white/50 font-normal mt-0.5" data-testid="options-upgrade-plan-monthly-interval">
                          {activePrices.monthly.interval}
                        </div>
                      </div>
                    </div>
                    <div className="mt-auto pt-4">
                      <Button
                        onClick={() => handlePlanAction('monthly')}
                        disabled={isLoading}
                        className="h-9 w-full rounded-full bg-white text-xs font-bold text-black hover:bg-white/90 disabled:opacity-50"
                        data-testid="options-upgrade-stripe-monthly-button"
                      >
                        {isLoading && selectedPlan === 'monthly' ? (
                          <>
                            <Spinner size={18} className="mr-2 animate-spin" />
                            Processando...
                          </>
                        ) : (
                          "Assinar mensal"
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* 2. Yearly Plan */}
                  <div className="relative flex h-full min-w-0 flex-col rounded-2xl border border-[#F0B90B]/35 bg-gradient-to-br from-[#F0B90B]/18 via-[#0B1220] to-[#10243F] p-4 shadow-[0_18px_50px_rgba(240,185,11,0.1)]" data-testid="options-upgrade-plan-yearly-card">
                    <div className="flex items-start justify-between gap-2">
                      <p className="pt-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/55" data-testid="options-upgrade-plan-yearly-label">YEARLY</p>
                      <div className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#F0B90B]/35 bg-[#F0B90B]/16 px-2 py-1 text-[9px] font-bold uppercase text-[#F7E2A0]" data-testid="options-upgrade-best-value-badge">
                      <Star size={12} weight="fill" />
                      MELHOR VALOR
                      </div>
                    </div>
                    <div className="pt-2.5">
                        <div className="whitespace-nowrap text-2xl font-black text-white lg:text-3xl" data-testid="options-upgrade-plan-yearly-price">
                          {activePrices.yearly.formatted}
                        </div>
                        <div className="text-xs text-white/55 font-normal mt-0.5" data-testid="options-upgrade-plan-yearly-interval">
                          {activePrices.yearly.interval}
                        </div>
                    </div>
                    <div className="mt-auto pt-4">
                      <Button
                        onClick={() => handlePlanAction('yearly')}
                        disabled={isLoading}
                        className="h-9 w-full rounded-full bg-[#F0B90B] text-xs font-bold text-black hover:bg-[#f5c73d] disabled:opacity-50"
                        data-testid="options-upgrade-stripe-yearly-button"
                      >
                        {isLoading && selectedPlan === 'yearly' ? (
                          <>
                            <Spinner size={18} className="mr-2 animate-spin" />
                            Processando...
                          </>
                        ) : (
                          "Assinar anual"
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* 3. Lifetime Plan (Early Access Exclusivo) */}
                  <div className="relative flex h-full min-w-0 flex-col rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/20 via-[#0B1220] to-[#120B24] p-4 shadow-[0_18px_50px_rgba(168,85,247,0.09)]" data-testid="options-upgrade-plan-lifetime-card">
                    <div className="flex items-start justify-between gap-2">
                      <p className="pt-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/55" data-testid="options-upgrade-plan-lifetime-label">LIFETIME</p>
                      <div className="inline-flex shrink-0 items-center gap-1 rounded-full border border-purple-400/35 bg-purple-500/15 px-2 py-1 text-[9px] font-bold uppercase text-purple-300" data-testid="options-upgrade-lifetime-badge">
                      <Crown size={12} weight="fill" />
                      EARLY ACCESS
                      </div>
                    </div>
                    <div className="pt-2.5">
                        <span className="inline-flex rounded-full border border-amber-400/20 bg-amber-400/10 px-2 py-0.5 text-[9px] font-bold text-amber-400/90" data-testid="options-upgrade-lifetime-slots">
                          Limitado a 20 vagas
                        </span>
                      <div className="pt-2">
                        <div className="whitespace-nowrap text-2xl font-black text-white lg:text-3xl" data-testid="options-upgrade-plan-lifetime-price">
                          {activePrices.lifetime.formatted}
                        </div>
                        <div className="text-xs text-white/55 font-normal mt-0.5" data-testid="options-upgrade-plan-lifetime-interval">
                          {activePrices.lifetime.interval}
                        </div>
                      </div>
                      <p className="pt-1.5 text-[11px] leading-4 text-white/60">
                        Acesso PRO vitalício sem assinatura recorrente.
                      </p>
                    </div>
                    <div className="mt-auto pt-3">
                      <Button
                        onClick={() => handlePlanAction('lifetime')}
                        className="h-9 w-full rounded-full border border-purple-500/40 bg-purple-600/20 text-xs font-bold text-purple-200 transition-colors hover:bg-purple-600/30"
                        data-testid="options-upgrade-lifetime-button"
                      >
                        Garantir Lifetime
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Early Access Discretionary Note */}
                <div className="space-y-0.5 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-[11px] leading-4 text-white/50" data-testid="options-upgrade-early-access-note">
                  <p>
                    Early Access válido até 31/12/2026. Planos/preços serão atualizados em 2027.
                  </p>
                  <p className="text-white/40">
                    Lifetime não será oferecido após Early Access.
                  </p>
                </div>
              </div>

              <div className="flex h-full min-w-0 flex-col rounded-2xl border border-white/10 bg-black/22 p-4 backdrop-blur-sm" data-testid="options-upgrade-benefits-card">
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/58" data-testid="options-upgrade-benefits-title">
                    <Lightning size={12} weight="fill" className="text-[#F0B90B]" />
                    {copy.benefitsTitle}
                  </div>
                  <div className="space-y-2" data-testid="options-upgrade-benefits-list">
                    {copy.benefits.map((benefit, index) => (
                      <div key={`${benefit}-${index}`} className="flex items-start gap-2.5 rounded-lg border border-white/6 bg-white/[0.025] px-3 py-2.5 text-sm leading-5 text-white/72" data-testid={`options-upgrade-benefit-${index + 1}`}>
                        <Check size={15} className="mt-0.5 shrink-0 text-[#F0B90B]" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                  {copy.disclaimer && <p className="text-xs leading-5 text-white/52" data-testid="options-upgrade-disclaimer">{copy.disclaimer}</p>}
                </div>

              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        onClick={(e) => { if (e.target === e.currentTarget && !isLoading) onClose(); }}
        data-testid="upgrade-modal-overlay"
      >
        <div 
          className={`bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-8 w-full mx-4 relative max-h-[92vh] overflow-y-auto ${isYearlyOnly ? 'max-w-md' : 'max-w-4xl'}`}
          data-testid="upgrade-modal-stripe"
        >
          <button
            onClick={handleBack}
            disabled={isLoading}
            className="absolute top-4 left-4 text-white/40 hover:text-white transition-colors disabled:opacity-50"
            data-testid="upgrade-modal-back"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>

          <button
            onClick={onClose}
            disabled={isLoading}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors disabled:opacity-50"
            data-testid="upgrade-modal-close"
          >
            <X size={20} weight="bold" />
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-[#635BFF]/20 to-[#8B5CF6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard size={32} weight="fill" className="text-[#635BFF]" />
            </div>
            {isYearlyOnly ? (
              <>
                <h2 className="text-2xl font-black font-heading tracking-tight mb-3">Upgrade to PRO Yearly</h2>
                <p className="text-white/60">
                  {isUpgrade 
                    ? "Unlock the full trading experience with an exclusive upgrade discount."
                    : "Save 26% and unlock the full trading experience."
                  }
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-black font-heading tracking-tight mb-1">Choose your plan</h2>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-[#F6D365]">Condições especiais para participantes do Early Access.</p>
                  <p className="text-[11px] text-white/40">Disponíveis até 31 de dezembro de 2026.</p>
                </div>
              </>
            )}
          </div>

          {/* Currency Selector */}
          {!isYearlyOnly && (
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-white/5">
              <span className="text-xs text-white/50">Pagar em:</span>
              <div className="inline-flex items-center rounded-md border border-white/10 bg-black/40 p-0.5">
                <button
                  type="button"
                  onClick={() => setCurrency('BRL')}
                  className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                    currency === 'BRL' ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                  }`}
                >
                  BRL
                </button>
                <button
                  type="button"
                  onClick={() => setCurrency('USD')}
                  className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                    currency === 'USD' ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                  }`}
                >
                  USD
                </button>
                <button
                  type="button"
                  onClick={() => setCurrency('CRYPTO')}
                  className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                    currency === 'CRYPTO' ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                  }`}
                >
                  CRYPTO
                </button>
              </div>
            </div>
          )}

          {/* Plans Grid */}
          <div className={isYearlyOnly ? 'mb-6' : 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'}>
            {/* Monthly Plan */}
            {!isYearlyOnly && (
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 flex flex-col justify-between">
                <div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-white">Monthly</h3>
                      <span className="text-[10px] text-white/40 border border-white/10 px-2 py-0.5 rounded-full">Early Access</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-black text-white">{activePrices.monthly.formatted}</span>
                      <span className="text-white/40 text-xs">{activePrices.monthly.interval}</span>
                    </div>
                  </div>
                  <ul className="text-xs text-white/60 space-y-2 mb-6">
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-[#00E676] shrink-0" />
                      Track every trade without limits
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-[#00E676] shrink-0" />
                      Behavioral insights on your patterns
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-[#00E676] shrink-0" />
                      Identify profitable setups
                    </li>
                  </ul>
                </div>
                <Button
                  onClick={() => handlePlanAction('monthly')}
                  disabled={isLoading}
                  className="w-full bg-white text-black font-bold hover:bg-gray-200 disabled:opacity-50 text-xs py-2 rounded-full"
                  data-testid="stripe-monthly-btn"
                >
                  {isLoading && selectedPlan === 'monthly' ? (
                    <>
                      <Spinner size={16} className="animate-spin mr-2" />
                      Processando...
                    </>
                  ) : (
                    'Assinar mensal'
                  )}
                </Button>
              </div>
            )}

            {/* Yearly Plan */}
            <div className={`bg-gradient-to-b from-[#635BFF]/15 to-[#0A0A0A] border ${isYearlyOnly ? 'border-2' : ''} border-[#635BFF]/40 rounded-xl p-5 flex flex-col justify-between relative shadow-[0_12px_40px_rgba(99,91,255,0.15)]`}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] text-white text-[10px] font-bold px-3 py-0.5 rounded-full">
                MELHOR VALOR
              </div>
              <div>
                <div className="mb-4 mt-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-1.5">
                      Yearly
                      <Star size={14} weight="fill" className="text-[#635BFF]" />
                    </h3>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-white">{activePrices.yearly.formatted}</span>
                    <span className="text-white/40 text-xs">{activePrices.yearly.interval}</span>
                  </div>
                </div>
                <ul className="text-xs text-white/60 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-[#00E676] shrink-0" />
                    Acesso anual completo ao GENI PRO
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-[#00E676] shrink-0" />
                    Todos os recursos atuais e futuros
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-[#00E676] shrink-0" />
                    Economia de 26% vs mensal
                  </li>
                </ul>
              </div>
              <Button
                onClick={() => handlePlanAction('yearly')}
                disabled={isLoading}
                className="w-full bg-[#635BFF] hover:bg-[#534be0] text-white font-bold disabled:opacity-50 text-xs py-2 rounded-full"
                data-testid="stripe-yearly-btn"
              >
                {isLoading && selectedPlan === 'yearly' ? (
                  <>
                    <Spinner size={16} className="animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  'Assinar anual'
                )}
              </Button>
            </div>

            {/* Lifetime Plan */}
            {!isYearlyOnly && (
              <div className="bg-gradient-to-b from-purple-900/20 to-[#0A0A0A] border border-purple-500/30 rounded-xl p-5 flex flex-col justify-between relative shadow-[0_12px_40px_rgba(168,85,247,0.1)]">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full">
                  EARLY ACCESS
                </div>
                <div>
                  <div className="mb-4 mt-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-1.5">
                        Lifetime
                        <Crown size={14} weight="fill" className="text-purple-400" />
                      </h3>
                      <span className="text-[9px] font-bold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-1.5 py-0.5 rounded-full">
                        20 vagas
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-black text-white">{activePrices.lifetime.formatted}</span>
                      <span className="text-white/40 text-[10px]">{activePrices.lifetime.interval}</span>
                    </div>
                  </div>
                  <ul className="text-xs text-white/60 space-y-2 mb-6">
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-[#00E676] shrink-0" />
                      Acesso PRO vitalício sem renovações
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-[#00E676] shrink-0" />
                      Exclusivo durante o Early Access
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={14} className="text-[#00E676] shrink-0" />
                      Limitado a 20 vagas
                    </li>
                  </ul>
                </div>
                <Button
                  onClick={() => handlePlanAction('lifetime')}
                  className="w-full border border-purple-500/40 bg-purple-600/20 text-purple-200 font-bold hover:bg-purple-600/30 text-xs py-2 rounded-full transition-all"
                  data-testid="stripe-lifetime-btn"
                >
                  Garantir Lifetime
                </Button>
              </div>
            )}
          </div>

          {/* Early Access Note */}
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-[11px] text-white/50 leading-relaxed text-center space-y-0.5 mb-5">
            <p>
              Os valores de Early Access são válidos até 31/12/2026. Em 2027, planos e preços serão atualizados.
            </p>
            <p className="text-white/40">
              O plano Lifetime é exclusivo do Early Access e não será oferecido após esse período.
            </p>
          </div>
          <div className="text-center space-y-2">
            <p className="text-white/40 text-xs">
              Pay with credit card. Your account will be upgraded instantly.
            </p>
            <button
              onClick={handleBack}
              disabled={isLoading}
              className="text-white/50 hover:text-white text-xs underline transition-colors disabled:opacity-50"
            >
              Use a different payment method
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP: Redirecting to Stripe
  if (step === 'redirecting') {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        data-testid="upgrade-modal-redirecting"
      >
        <div className="bg-[#121212] border border-white/10 rounded-sm p-8 w-full max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-[#635BFF]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Spinner size={32} weight="bold" className="text-[#635BFF] animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{copy.redirectingTitle}</h2>
          <p className="text-white/60 text-sm">
            {copy.redirectingBody}
          </p>
        </div>
      </div>
    );
  }

  // Fallback (should never reach here)
  return null;
}
